import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, BarChart3, GitBranch, ListChecks, FileText, Layers } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import BrochureButton from "@/components/BrochureButton";
import { BRAND, IMAGES, MODULES, MODULE_REPORTS } from "@/lib/constants";

const moduleIcons = {
  "product-quality": ShieldCheck,
  "process-compliance": BarChart3,
  "rca": GitBranch,
  "actions": ListChecks,
};

export default function Home() {
  return (
    <>
      {/* HERO */}
      <section data-testid="hero-section" className="relative isolate overflow-hidden bg-[#0A1128] text-white">
        <div className="absolute inset-0 -z-10">
          <img
            src={IMAGES.hero_factory}
            alt="Apparel manufacturing floor"
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/80 to-transparent" />
        </div>

        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-24 sm:px-6 md:py-32 lg:grid-cols-12 lg:px-8">
          <div className="lg:col-span-8 pc-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-[#E8772E]" />
              Now Launching · By PrimeCore Solutions
            </div>
            <h1 className="mt-6 font-serif-display text-4xl font-medium leading-[1.05] sm:text-5xl lg:text-7xl">
              Q-Wis<span className="text-[#E8772E]">.</span> Wisdom on
              <br className="hidden sm:block" /> Quality &amp; Sustainability.
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
              The single source of truth for product &amp; operational quality.
              A practical Windows-based platform that replaces scattered Excel reports — so your teams see the same numbers, act on the same facts, and improve together.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                data-testid="hero-cta-demo"
                to="/contact?intent=demo"
                className="inline-flex items-center gap-2 rounded-md bg-[#E8772E] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#E8772E]/20 transition-all hover:bg-[#D66826]"
              >
                Request a Demo <ArrowRight size={16} />
              </Link>
              <BrochureButton variant="ghost" label="Download Brochure" />
              <Link
                data-testid="hero-cta-product"
                to="/product"
                className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-white/5 px-6 py-3.5 text-sm font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/10"
              >
                Explore the Modules
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-white/60">
              <span>70+ Years Combined QA &amp; IT Expertise</span>
              <span className="hidden h-3 w-px bg-white/20 sm:inline-block" />
              <span>Built for Global Apparel Brands</span>
              <span className="hidden h-3 w-px bg-white/20 sm:inline-block" />
              <span>One-Time Payment + AMC</span>
            </div>
          </div>

          <div className="hidden lg:col-span-4 lg:flex lg:items-center lg:justify-end">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 backdrop-blur-md">
              <img
                src={BRAND.logos.qwis}
                alt="Q-Wis logo"
                className="h-36 w-36 object-contain rounded-[22%]"
              />
              <p className="mt-5 max-w-[14rem] font-serif-display text-xl text-white/90">
                The single source of truth for product &amp; process quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEM */}
      <section data-testid="problem-section" className="bg-[#2E3744] py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Reality on the Floor"
            title="Sounds familiar?"
            lede="If any of these are hitting close to home — you're not alone. We've lived this. And we've built the answer."
          />
          <ul className="mt-14 grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2 pc-stagger">
            {[
              "Tired of customer final inspection / DC failures and complaints — and losing customer confidence?",
              "At the risk of losing self-empowerment on shipment release and paying for 3rd-party inspections?",
              "Sea-freight shipments being converted to air at huge cost — because of failures or rework?",
              "Top management losing confidence in quality performance?",
              "Can't pinpoint an issue across dozens of manual documents and Excel reports?",
              "Lots of opinions on issues, but no hard facts and figures — time wasted validating opinions?",
              "Reactive firefighting — the same issue recurring in the same or different locations?",
              "SOPs and processes on paper, but you can't track, improve or sustain process health?",
              "Many forums and discussions on issues and improvement — but no records of action and outcome?",
              "Expecting to digitize quality, but can't make sense of the ROI?",
            ].map((q, i) => (
              <li
                key={i}
                className="flex items-start gap-4 rounded-md border border-white/10 bg-white/[0.03] p-5 text-sm leading-relaxed text-white/85 transition-colors hover:bg-white/[0.06]"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E8772E]/15 text-xs font-semibold text-[#E8772E]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{q}</span>
              </li>
            ))}
          </ul>

          <div className="mt-14 rounded-lg border border-[#E8772E]/30 bg-[#E8772E]/[0.08] p-8 text-center">
            <p className="font-serif-display text-2xl text-white sm:text-3xl">
              We've lived the problem — and we've built the solution for you.
            </p>
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section data-testid="modules-section" className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <SectionHeading
              eyebrow="Q-Wis Modules"
              title="Four integrated modules. One Windows-based solution."
              lede="Built end-to-end for apparel manufacturers — from raw material onboarding to shipment, audit to action."
            />
            <Link
              data-testid="modules-explore-link"
              to="/product"
              className="group inline-flex items-center gap-2 self-start whitespace-nowrap text-sm font-semibold text-[#0A1128] hover:text-[#E8772E] md:self-end"
            >
              Explore each module
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 pc-stagger">
            {MODULES.map((m, i) => {
              const Icon = moduleIcons[m.id];
              return (
                <Link
                  to="/product"
                  key={m.id}
                  data-testid={`module-card-${m.id}`}
                  className="group flex flex-col rounded-lg border border-[#E2E8F0] bg-white p-7 transition-all hover:-translate-y-1 hover:border-[#0A1128] hover:shadow-lg"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#0A1128] text-white transition-colors group-hover:bg-[#E8772E]">
                      <Icon size={20} />
                    </div>
                    <span className="font-serif-display text-3xl font-medium text-[#E8772E]/80">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-6 font-serif-display text-2xl leading-tight text-[#0A1128]">
                    {m.name}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#475569]">
                    {m.description}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT Q-WIS REALLY IS — Single Source of Truth */}
      <section data-testid="sot-section" className="bg-[#0A1128] py-20 text-white sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
                What Q-Wis really is
              </p>
              <h2 className="mt-4 font-serif-display text-3xl font-medium leading-tight sm:text-4xl lg:text-5xl">
                A single source of truth for product &amp; operational quality.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-white/80">
                Most teams still rely on Excel and isolated reports. Q-Wis goes beyond data collection — it gives you a structured, standardised, secure system so decisions are consistent and improvement is sustainable.
              </p>
              <p className="mt-4 text-base leading-relaxed text-white/70">
                Not a high-cost application. A practical, scalable operational excellence platform — built on strong quality and governance principles.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {[
                  {
                    t: "Structured. Standardised. Secure.",
                    d: "Detailed inspection and process data captured with integrity, traceability and security — replacing fragmented spreadsheets.",
                  },
                  {
                    t: "Macro & micro analysis, together.",
                    d: "From a single executive dashboard down to the exact operation that generated the defect — without switching tools.",
                  },
                  {
                    t: "Organisational learning, not personal knowledge.",
                    d: "A centralised library of problems, root causes, actions and solutions — referenced and reused across teams and facilities.",
                  },
                  {
                    t: "Accountability you can see.",
                    d: "Action plans, governance forums and management effectiveness tracked with visibility, consistency and follow-through.",
                  },
                ].map((c, i) => (
                  <div
                    key={i}
                    data-testid={`sot-card-${i}`}
                    className="rounded-md border border-white/10 bg-white/[0.04] p-6"
                  >
                    <p className="font-serif-display text-xl leading-snug text-white">{c.t}</p>
                    <p className="mt-3 text-sm leading-relaxed text-white/70">{c.d}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OBJECTION HANDLER — "Not ready yet" */}
      <section data-testid="readiness-section" className="relative isolate overflow-hidden bg-gradient-to-br from-[#E8772E] to-[#D66826] py-20 text-white sm:py-24">
        <div className="absolute inset-0 -z-10 opacity-[0.07]" aria-hidden="true">
          <div className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-white blur-3xl" />
          <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-white blur-3xl" />
        </div>
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-white/80">
            "We're not ready yet" — that's exactly the point
          </p>
          <h2 className="mt-5 font-serif-display text-3xl font-medium leading-tight sm:text-5xl">
            Q-Wis doesn't require you to be ready. Q-Wis is what makes you ready.
          </h2>
          <p className="mx-auto mt-7 max-w-3xl text-base leading-relaxed text-white/90 sm:text-lg">
            Many organisations worry they can't sustain a quality system yet. The truth is the opposite — <strong className="text-white">establishing Q-Wis is what standardises your processes, strengthens accountability, and creates the foundation for measurable improvement</strong> across the organisation.
          </p>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/80 sm:text-base">
            You don't need a perfect process to begin. You need a structured platform that <em>builds</em> the discipline as you use it. That's exactly what Q-Wis was designed to do.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              data-testid="readiness-cta-demo"
              to="/contact?intent=demo"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A1128] px-7 py-3.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-[#13204A]"
            >
              Talk to us — no pressure <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* REPORTS & ANALYTICS — Home summary (stacked-slides teaser) */}
      <section data-testid="reports-section" className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Reports & Analytics"
            title="BI-level insight, built in. No add-on subscription, no extra licence."
            lede="Every module comes with its own reports — drill from macro numbers to the exact operation, using tables, bar charts and trend lines already inside Q-Wis."
          />

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4 pc-stagger">
            {MODULES.map((m) => {
              const info = MODULE_REPORTS[m.id];
              const stories = info?.stories || [];
              const hasReports = stories.length > 0;
              const thumb = stories[0]?.steps?.[0]?.src;
              const storyCount = stories.length;
              return (
                <Link
                  key={m.id}
                  to={`/product#reports-${m.id}`}
                  data-testid={`reports-summary-${m.id}`}
                  className="group relative flex flex-col overflow-hidden rounded-lg border border-[#E2E8F0] bg-white transition-all hover:-translate-y-1 hover:shadow-xl"
                >
                  {/* Stacked slides preview */}
                  <div className="relative h-48 overflow-hidden bg-gradient-to-br from-[#F1F5F9] to-[#F8FAFC] px-6 py-6">
                    {hasReports ? (
                      <>
                        {/* Back slide */}
                        <div className="absolute right-4 top-8 h-32 w-40 rotate-[-6deg] rounded-md border border-[#E2E8F0] bg-white shadow-md transition-transform duration-500 group-hover:rotate-[-9deg] group-hover:-translate-x-2" />
                        {/* Middle slide */}
                        <div className="absolute right-8 top-6 h-32 w-40 rotate-[3deg] rounded-md border border-[#E2E8F0] bg-white shadow-lg transition-transform duration-500 group-hover:rotate-[6deg]" />
                        {/* Front slide with actual thumbnail */}
                        <div className="absolute right-6 top-4 h-32 w-40 overflow-hidden rounded-md border border-[#0A1128]/20 bg-white shadow-xl transition-transform duration-500 group-hover:translate-x-2 group-hover:-translate-y-1">
                          <div className="flex items-center gap-1 border-b border-[#E2E8F0] bg-[#F8FAFC] px-2 py-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-[#FB7185]" />
                            <span className="h-1.5 w-1.5 rounded-full bg-[#FACC15]" />
                            <span className="h-1.5 w-1.5 rounded-full bg-[#34D399]" />
                          </div>
                          <img
                            src={thumb}
                            alt=""
                            className="h-full w-full object-cover object-left-top"
                          />
                        </div>
                        <span className="absolute left-6 top-6 inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-white">
                          <Layers size={11} />
                          {storyCount} {storyCount === 1 ? "story" : "stories"}
                        </span>
                      </>
                    ) : (
                      <div className="flex h-full items-center justify-center text-center">
                        <span className="inline-flex items-center gap-2 rounded-full border border-dashed border-[#94A3B8] px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#64748B]">
                          Coming soon
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col justify-between p-5">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#E8772E]">
                        {m.short}
                      </p>
                      <p className="mt-2 font-serif-display text-lg leading-tight text-[#0A1128]">
                        {m.name}
                      </p>
                    </div>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0A1128] group-hover:text-[#E8772E]">
                      See full reports
                      <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* 8 BI capability bullets */}
          <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-2">
            {[
              {
                t: "BI-level analysis at no extra cost",
                d: "Drilldowns, bar charts and trend analysis — built in. No additional subscription, no extra BI licence.",
              },
              {
                t: "Drill down by manufacturing location",
                d: "Quality KPIs at Plant, Process, Module, Section, Line — even Machine level — when you need it.",
              },
              {
                t: "Drill down by commercial angle",
                d: "Customer, Product Category and Style-level KPIs — see exactly where quality is moving the P&L.",
              },
              {
                t: "Defects tied to the operation that caused them",
                d: "Problem solving happens at the point of occurrence — not in a meeting room three days later.",
              },
              {
                t: "Defect migration across processes",
                d: "See where defects appear, disappear and re-emerge — so you know exactly where to focus your effort.",
              },
              {
                t: "Process & owner-level KPIs",
                d: "Department-level process KPIs including CAP% — corrective action submission for every shortfall.",
              },
              {
                t: "Forum & action efficiency",
                d: "Action completion at department and individual level. Management efficiency in problem-solving — measured, not assumed.",
              },
              {
                t: "Your own knowledge library",
                d: "Problems, operations, KPI shortfalls and solutions — stacked, searchable and shareable across the whole organisation.",
              },
            ].map((b, i) => (
              <div
                key={i}
                data-testid={`reports-bullet-${i}`}
                className="rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-6"
              >
                <p className="font-serif-display text-lg leading-tight text-[#0A1128]">{b.t}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#475569]">{b.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-wrap items-center justify-center gap-4">
            <Link
              data-testid="reports-cta-product"
              to="/product"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A1128] px-7 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#13204A]"
            >
              See all reports on the Product page <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY Q-WIS */}
      <section data-testid="why-section" className="bg-[#F8FAFC] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why Q-Wis"
            title="Built so the math finally makes sense."
          />

          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-12">
            <div data-testid="why-card-pricing" className="md:col-span-7 rounded-lg bg-[#0A1128] p-10 text-white">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
                Predictable Pricing
              </p>
              <h3 className="mt-4 font-serif-display text-3xl leading-tight sm:text-4xl">
                No user-based subscriptions. Pricing is location-based — so
                scaling is predictable, not punishing.
              </h3>
              <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/70">
                One-time payment plus AMC. No recurring licensing burden, no
                penalty for adding more auditors, no lock-in to user counts.
              </p>
            </div>

            <div data-testid="why-card-windows" className="md:col-span-5 rounded-lg border border-[#E2E8F0] bg-white p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
                Practical, Not Theoretical
              </p>
              <h3 className="mt-4 font-serif-display text-2xl leading-tight text-[#0A1128] sm:text-3xl">
                A Windows-based solution that fits the way your floor actually works.
              </h3>
              <p className="mt-5 text-sm leading-relaxed text-[#475569]">
                Both real-time and data entry — at the lowest investment.
                Configure any defined inspection stage across the value chain.
              </p>
            </div>

            <div data-testid="why-card-builders" className="md:col-span-5 overflow-hidden rounded-lg">
              <img src={IMAGES.quality_inspection} alt="Quality inspection" className="h-full w-full object-cover" />
            </div>

            <div data-testid="why-card-experience" className="md:col-span-7 rounded-lg border border-[#E2E8F0] bg-white p-10">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
                Built by Industry Veterans
              </p>
              <h3 className="mt-4 font-serif-display text-2xl leading-tight text-[#0A1128] sm:text-3xl">
                Q-Wis is shaped by people who lived these problems for decades.
              </h3>
              <ul className="mt-6 space-y-3 text-sm text-[#334155]">
                {[
                  "70+ years combined QA &amp; IT expertise",
                  "Designed at a premier Sri Lankan apparel exporter with global manufacturing footprint",
                  "Aligned with the requirements of renowned global apparel brands",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[#E8772E]" />
                    <span dangerouslySetInnerHTML={{ __html: t }} />
                  </li>
                ))}
              </ul>
              <Link
                data-testid="why-cta-about"
                to="/about"
                className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#0A1128] hover:text-[#E8772E]"
              >
                Meet the team <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* BROCHURE STRIP */}
      <section data-testid="brochure-section" className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-center gap-10 rounded-lg border border-[#E2E8F0] bg-gradient-to-br from-[#F8FAFC] to-white p-10 lg:grid-cols-12 lg:p-14">
            <div className="lg:col-span-7">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
                Take it with you
              </p>
              <h3 className="mt-4 font-serif-display text-3xl leading-tight text-[#0A1128] sm:text-4xl">
                One page. Everything your team needs to evaluate Q-Wis.
              </h3>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#475569] sm:text-base">
                Get the printable Q-Wis brochure — the four modules, pricing model, and what's included. Share it with your COO, Head of Quality, or sourcing team in one click.
              </p>
              <div className="mt-7">
                <BrochureButton size="lg" />
              </div>
            </div>
            <div className="hidden lg:col-span-5 lg:flex lg:justify-end">
              <div className="relative">
                <div className="absolute -inset-3 rounded-lg bg-[#E8772E]/10 blur-2xl" />
                <a href="/qwis-brochure.pdf" target="_blank" rel="noreferrer" className="relative block">
                  <div className="relative aspect-[210/297] w-52 overflow-hidden rounded-md border border-[#E2E8F0] bg-white shadow-2xl shadow-[#0A1128]/15 transition-transform hover:scale-105">
                    <div className="h-1/4 w-full bg-[#0A1128]" />
                    <div className="space-y-2 px-3 py-3">
                      <div className="h-2 w-3/4 rounded-full bg-[#0A1128]/80" />
                      <div className="h-1.5 w-full rounded-full bg-[#E2E8F0]" />
                      <div className="h-1.5 w-5/6 rounded-full bg-[#E2E8F0]" />
                      <div className="mt-3 grid grid-cols-3 gap-1.5">
                        <div className="h-8 rounded bg-[#2E3744]" />
                        <div className="h-8 rounded bg-[#2E3744]" />
                        <div className="h-8 rounded bg-[#2E3744]" />
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-1.5">
                        <div className="h-10 rounded border border-[#E2E8F0]" />
                        <div className="h-10 rounded border border-[#E2E8F0]" />
                        <div className="h-10 rounded border border-[#E2E8F0]" />
                        <div className="h-10 rounded border border-[#E2E8F0]" />
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-1.5">
                        <div className="h-6 rounded bg-[#0A1128]" />
                        <div className="h-6 rounded bg-[#0A1128]" />
                        <div className="h-6 rounded bg-[#0A1128]" />
                      </div>
                    </div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section data-testid="cta-section" className="bg-[#0A1128] py-20 text-white sm:py-24">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-4 sm:px-6 lg:flex-row lg:items-end lg:justify-between lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
              Enable your quality with Q-Wis
            </p>
            <h2 className="mt-4 font-serif-display text-3xl leading-tight sm:text-5xl">
              Smarter quality. Stronger processes. Sustainable performance.
            </h2>
          </div>
          <div className="flex flex-wrap items-center gap-4">
            <Link
              data-testid="footer-cta-demo"
              to="/contact?intent=demo"
              className="inline-flex items-center gap-2 rounded-md bg-[#E8772E] px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-[#E8772E]/20 transition-colors hover:bg-[#D66826]"
            >
              Request a Demo <ArrowRight size={16} />
            </Link>
            <Link
              data-testid="footer-cta-pricing"
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-transparent px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/5"
            >
              See Pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
